import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Workflow API — Smart Visit Workflow Engine endpoints
 *
 * Base paths:
 *   POST   /api/organisation/workflow/actions       — Log completed action
 *   GET    /api/organisation/workflow/suggestions    — Get next-best-action suggestions
 *   GET    /api/organisation/workflow/patterns       — Get learned patterns
 *   PUT    /api/organisation/workflow/preferences    — Update auto-progression prefs
 * ═══════════════════════════════════════════════════════════════════════
 */

// ── Action Logging (fire-and-forget) ─────────────────────────────────

/**
 * Log a completed visit action for the prediction engine.
 * This is fire-and-forget — failures are silently swallowed.
 *
 * @param {string} visitId - Visit this action belongs to
 * @param {string} action  - Action enum (e.g. 'ADD_VITALS', 'MARK_COMPLETED')
 * @param {object} [context] - Optional context metadata
 */
export const logWorkflowAction = async (visitId, action, context = null) => {
  try {
    const body = {
      visit_id: visitId,
      action,
    };
    if (context) body.context = context;

    // Fire and forget — don't await, don't throw
    apiClient.post('/api/organisation/workflow/actions', body).catch((err) => {
      console.debug('[WorkflowAPI] Action log failed (non-fatal):', err.message);
    });
  } catch (err) {
    // Truly silent — workflow tracking should never interrupt the user
    console.debug('[WorkflowAPI] Action log error (non-fatal):', err.message);
  }
};

// ── Suggestions ──────────────────────────────────────────────────────

/**
 * Get next-best-action suggestions for a visit.
 *
 * @param {string} visitId     - Visit to get suggestions for
 * @param {string} [lastAction] - Last action performed (auto-detected if omitted)
 * @param {string} [visitStatus] - Current visit status
 * @returns {Promise<{visit_id, current_stage, suggestions[], auto_progress_enabled}>}
 */
export const fetchWorkflowSuggestions = async (visitId, lastAction = null, visitStatus = null) => {
  try {
    const params = { visit_id: visitId };
    if (lastAction) params.last_action = lastAction;
    if (visitStatus) params.visit_status = visitStatus;

    const response = await apiClient.get('/api/organisation/workflow/suggestions', { params });
    return response.data;
  } catch (err) {
    console.warn('[WorkflowAPI] Suggestions fetch failed:', err.message);
    // Return empty suggestions on failure — never block the UI
    return {
      visit_id: visitId,
      current_stage: null,
      suggestions: [],
      auto_progress_enabled: false,
    };
  }
};

// ── Patterns ─────────────────────────────────────────────────────────

/**
 * Get the user's learned workflow patterns.
 *
 * @returns {Promise<{member_id, role, total_actions_tracked, top_sequences[], ...}>}
 */
export const fetchWorkflowPatterns = async () => {
  try {
    const response = await apiClient.get('/api/organisation/workflow/patterns');
    return response.data;
  } catch (err) {
    console.error('[WorkflowAPI] Patterns fetch failed:', err);
    throw err;
  }
};

// ── Preferences ──────────────────────────────────────────────────────

/**
 * Fetch current workflow preferences (includes preferred_prescription_tab).
 */
export const fetchWorkflowPreferences = async () => {
  try {
    const response = await apiClient.get('/api/organisation/workflow/preferences');
    return response.data;
  } catch (err) {
    console.error('[WorkflowAPI] Preferences fetch failed:', err);
    throw err;
  }
};

/**
 * Update auto-progression preferences.
 *
 * @param {object} prefs - { auto_progress_enabled, auto_progress_threshold, suppressed_suggestions }
 * @returns {Promise<{member_id, auto_progress_enabled, auto_progress_threshold, ...}>}
 */
export const updateWorkflowPreferences = async (prefs) => {
  try {
    const response = await apiClient.put('/api/organisation/workflow/preferences', prefs);
    return response.data;
  } catch (err) {
    console.error('[WorkflowAPI] Preferences update failed:', err);
    throw err;
  }
};

/**
 * Record which prescription tab the user actually used.
 * Backend auto-flips default after 2 consecutive uses of the other tab.
 *
 * @param {'add'|'upload'} tabUsed
 */
export const recordPrescriptionTabUsage = async (tabUsed) => {
  try {
    const response = await apiClient.post('/api/organisation/workflow/prescription-tab-usage', {
      tab_used: tabUsed,
    });
    return response.data;
  } catch (err) {
    console.error('[WorkflowAPI] Prescription tab usage failed:', err);
    // Non-blocking — don't throw
  }
};


// ── Action Constants (mirrors backend VisitAction enum) ──────────────

export const WORKFLOW_ACTIONS = {
  // Status transitions
  VISIT_CREATED: 'VISIT_CREATED',
  MARK_IN_PROGRESS: 'MARK_IN_PROGRESS',
  MARK_COMPLETED: 'MARK_COMPLETED',
  MARK_CANCELLED: 'MARK_CANCELLED',

  // Metadata actions
  ADD_VITALS: 'ADD_VITALS',
  ADD_NOTES: 'ADD_NOTES',
  ADD_SYMPTOMS: 'ADD_SYMPTOMS',
  ADD_PRESCRIPTION: 'ADD_PRESCRIPTION',
  ADD_REPORT: 'ADD_REPORT',

  // Navigation
  VIEW_PATIENT_HISTORY: 'VIEW_PATIENT_HISTORY',
  OPEN_VISIT: 'OPEN_VISIT',
  START_NEXT_VISIT: 'START_NEXT_VISIT',

  // Document generation
  GENERATE_PRESCRIPTION: 'GENERATE_PRESCRIPTION',

  // Billing
  INITIATE_BILLING: 'INITIATE_BILLING',
};

const workflowApi = {
  logWorkflowAction,
  fetchWorkflowSuggestions,
  fetchWorkflowPatterns,
  fetchWorkflowPreferences,
  updateWorkflowPreferences,
  recordPrescriptionTabUsage,
  WORKFLOW_ACTIONS,
};

export default workflowApi;
